import { LoanType, PoolListItem, PoolStatus } from '@/types/pool';
import { getAcceptedCounterOfferInfo } from './poolStatus';

export const totalBorrowed = (pools: PoolListItem[]): string => {
  const activeOrFundedPools = pools.filter(
    (pool) =>
      pool.status !== PoolStatus.REJECTED &&
      pool.status !== PoolStatus.CANCELED &&
      pool.status !== PoolStatus.CLOSED &&
      pool.status !== PoolStatus.FUNDING_REJECTED
  );

  return `$${activeOrFundedPools
    .reduce((sum, pool) => {
      const acceptedCounterInfo = getAcceptedCounterOfferInfo(pool.offers);
      const amount = acceptedCounterInfo?.amount ?? pool.loan_terms.amount;
      return sum + parseFloat(amount || '0');
    }, 0)
    .toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
};

/**
 * Calculate user's share percentage based on co-owners
 * @param hasCoOwners - Whether there are co-owners
 * @param coOwners - Array of co-owners with percentage
 * @returns User's share as string percentage
 */
export const calculateUserShare = (hasCoOwners: boolean, coOwners: Array<{ percentage: string }>): string => {
  if (!hasCoOwners) return '100';
  const totalCoOwnerShare = coOwners.reduce((sum, owner) => {
    const percentage = parseFloat(owner.percentage) || 0;
    return sum + percentage;
  }, 0);
  const userShare = Math.max(0, 100 - totalCoOwnerShare);
  return userShare.toString();
};

export const EQUIPOOL_INTEREST = 0.005; // Title fee percentage
export const MAX_MONTHS = 1200; // 100 years

// Fixed fees breakdown:
// Notary Fee: $300
// Escrow loan tie in fee: $150
// Escrow Messenger Fee: $100
// Escrow wire fee: $30
// Document preparation: $100
// Note and Deed of trust preparation: $300
// Title sub escrow charge: $90
// Title recording service fee: $23
// Title to record the Deed of trust: $450
// Title endorsement fee: $50
// Total: $1,593
export const CONSTANT_FEES = 1593;

export const MILLION = 1_000_000;

// Escrow fee thresholds (used by computeLoanDetails)
export const ESCROW_FEE_OVER_MILLION = 750;
export const ESCROW_FEE_UNDER_MILLION = 650;

/**
 * Compute loan details including repayments and ROI
 *
 * @param loanAmount principal loaned (P)
 * @param percent annual interest rate as percent (e.g. 6 => 6% p.a.)
 * @param type "interest-only" or "amortizing"
 * @param months term length in months (integer >= 1, <= 1200)
 * @param propertyValue property value for LTV
 * @param existingLoans array of existing loans for LTV
 * @param isRefinancing When true the existing-loan balances are NOT added to the numerator

 *
 * @returns Object containing:
 *   - overallRepay: Total overall amount that will be paid over entire loan term
 *   - monthlyRepay: Regular monthly payment amount
 *   - roiAmount: Total profit (overallRepay - initialOutlay)
 *   - roiPercent: ROI as decimal (e.g. 0.05 for 5%)
 *   - monthlyInterest: Monthly interest amount (see note below)
 *   - initialOutlay: Initial principal loaned
 *   - cashflows: Array of cashflows (negative at t=0, positive for repayments)
 *   - loanToValue: Loan to Value ratio as percentage
 *
 * Assumptions:
 * - percent is nominal annual rate, monthly_rate = percent/100/12
 *
 * Loan Types:
 * - "interest-only":
 *   * Monthly payments (months 1 to months-1) are interest only: P × monthly_rate
 *   * Final payment (month months) includes principal + final month's interest: P + (P × monthly_rate)
 *   * Total paid = principal + (months × monthly_interest)
 *   * monthlyInterest = P × monthly_rate (constant throughout the loan term)
 *
 * - "amortizing" :
 *   * Equal monthly payments of principal + interest throughout the term
 *   * Monthly payment = P × (r(1+r)^n) / ((1+r)^n - 1) where r is monthly rate, n is months
 *   * Payment composition changes over time:
 *     - Early payments: more interest, less principal (interest calculated on full remaining balance)
 *     - Later payments: less interest, more principal (as principal balance decreases)
 *   * The total monthly payment amount stays constant, but the interest/principal split shifts
 *   * monthlyInterest = P × monthly_rate (represents interest in the first payment only)
 *
 * Loan to Value (LTV) Calculation:
 * - Formula: (existing loans total + requested amount) / property value * 100%
 */

type LoanDetails = {
  closingCost: number;
  overallRepay: number;
  monthlyPayment: number;
  roiAmount: number;
  roiPercent: number;
  initialOutlay: number;
  cashflows: number[];
};
export const computeLoanDetails = (
  loanAmount: number,
  percent: number,
  type: LoanType | null,
  months: number
): LoanDetails => {
  if (loanAmount <= 0 || months < 1 || months > MAX_MONTHS || !type) {
    return {
      closingCost: 0,
      overallRepay: 0,
      monthlyPayment: 0,
      roiAmount: 0,
      roiPercent: 0,
      initialOutlay: 0,
      cashflows: [],
    };
  }

  const monthlyRate = percent / 100 / 12; // decimal, e.g. 0.06/12 = 0.005
  const P = loanAmount;

  // monthly_interest = P * monthlyRate
  // For interest-only: this is the actual interest paid each month (constant)
  // For amortization: this represents initial interest, but actual interest decreases over time
  const monthly_interest = P * monthlyRate;

  let monthly_repay = 0;
  let total_cash_inflow = 0;
  let cashflows: number[] = [];

  // cashflow convention: lender pays out at t=0 (negative), inflows positive
  cashflows.push(-P);

  switch (type) {
    case LoanType.INTEREST_ONLY: {
      monthly_repay = monthly_interest;
      // months 1..months-1 => interest only; final month => interest + principal
      for (let m = 1; m < months; m++) {
        cashflows.push(monthly_interest);
      }
      // final month: principal + interest
      const final_payment = monthly_interest + P;
      cashflows.push(final_payment);
      total_cash_inflow = monthly_interest * months + P;
      break;
    }
    case LoanType.AMORTIZING:
      // Amortizing loan: equal monthly payments of principal + interest
      // Early payments: more interest, less principal
      // Later payments: less interest, more principal
      // Monthly payment formula: P × (r(1+r)^n) / ((1+r)^n - 1)
      if (monthlyRate === 0) {
        // Zero interest: simple division
        monthly_repay = P / months;
        total_cash_inflow = P;
        // Generate cashflows: equal payment each month
        for (let m = 1; m <= months; m++) {
          cashflows.push(monthly_repay);
        }
      } else {
        const r = monthlyRate;
        const n = months;
        // Calculate monthly payment using amortization formula
        monthly_repay = (P * (r * Math.pow(1 + r, n))) / (Math.pow(1 + r, n) - 1);

        // Calculate amortization schedule month by month to get exact totals
        let remaining_balance = P;
        let total_interest_paid = 0;

        for (let m = 1; m <= months; m++) {
          // Interest for this month = remaining balance × monthly rate
          const interest_payment = remaining_balance * r;
          // Principal payment = monthly payment - interest
          const principal_payment = monthly_repay - interest_payment;
          // Update remaining balance
          remaining_balance = remaining_balance - principal_payment;
          total_interest_paid += interest_payment;

          // Add payment to cashflows
          cashflows.push(monthly_repay);
        }

        // Total cash inflow = principal + total interest paid
        total_cash_inflow = P + total_interest_paid;
      }
      break;
    default:
      return {
        closingCost: 0,
        overallRepay: 0,
        monthlyPayment: 0,
        roiAmount: 0,
        roiPercent: 0,
        initialOutlay: 0,
        cashflows: [],
      };
  }

  const is_greater_than_million = loanAmount >= MILLION;
  const escrow_fee = is_greater_than_million ? ESCROW_FEE_OVER_MILLION : ESCROW_FEE_UNDER_MILLION;

  const initial_outlay = P;
  const roi_amount = total_cash_inflow - initial_outlay;
  const roi_percent = initial_outlay === 0 ? 0 : roi_amount / initial_outlay;

  const closingCost = EQUIPOOL_INTEREST * loanAmount + escrow_fee + CONSTANT_FEES;

  return {
    closingCost: round2(closingCost),
    overallRepay: round2(total_cash_inflow),
    monthlyPayment: round2(monthly_repay),
    roiAmount: round2(roi_amount),
    roiPercent: round2(roi_percent * 100) / 100, // returned as decimal e.g. 0.05 => 0.05 (two-decimal)
    initialOutlay: round2(initial_outlay),
    cashflows: cashflows.map((c) => round2(c)),
  };
};

export const calculateLTV = (
  loanAmount: number,
  propertyValue: number | string,
  loansRemainingBalances: string[],
  isRefinancing = false
): number => {
  const propVal = parseFloat(propertyValue ? propertyValue.toString() : '0');

  if (!loanAmount || !propVal) {
    return 0;
  }

  if (isRefinancing) {
    return round2((loanAmount / propVal) * 100);
  }

  const totalExistingLoans = loansRemainingBalances.reduce((sum, balance) => sum + parseFloat(balance || '0'), 0);
  return round2(((totalExistingLoans + loanAmount) / propVal) * 100);
};

// return result (rounded to cents for human consumption, keep unrounded numbers could be preferable)
function round2(x: number) {
  return Math.round((x + Number.EPSILON) * 100) / 100;
}

export const getPoolTermsData = (
  pool: PoolListItem
): {
  requestedAmount: string;
  roiRate: string;
  loanType: LoanType;
  term: number;
  termMonths: number;
} => {
  const acceptedCounterInfo = getAcceptedCounterOfferInfo(pool.offers);

  const showRecalculatedValues =
    pool.status === PoolStatus.IN_ESCROW ||
    pool.status === PoolStatus.FUNDED ||
    (pool.status === PoolStatus.FUNDING_REJECTED && !!acceptedCounterInfo);

  return showRecalculatedValues
    ? {
        requestedAmount: acceptedCounterInfo?.amount || pool.loan_terms.amount,
        roiRate: acceptedCounterInfo?.roi_rate || pool.loan_terms.roi_rate,
        loanType: pool.loan_terms.loan_type,
        term: acceptedCounterInfo?.term_months || pool.loan_terms.term_months,
        termMonths: acceptedCounterInfo?.term_months || pool.loan_terms.term_months,
      }
    : {
        requestedAmount: pool.loan_terms.amount,
        roiRate: pool.loan_terms.roi_rate,
        loanType: pool.loan_terms.loan_type,
        term: pool.loan_terms.term_months,
        termMonths: pool.loan_terms.term_months,
      };
};
